import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Send, ShieldAlert, Loader2, Sparkles, AlertTriangle, RotateCcw, Trophy,
} from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import { API_URL } from '../config';
import '../styles/ScamSmart.css';
import '../styles/ScamSmartPractice.css';

const API = `${API_URL}/api`;
const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';
const CONSENT_KEY = 'ss_practice_consent_v1';

const VERDICT_LABEL = {
  defended: 'You defended yourself',
  partial: 'A close call — keep practising',
  fell_for_it: 'The scammer would have got you',
};

const ScamSmartPractice = ({ ssEmail }) => {
  const { scenarioId: pathScenarioId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [scenarioId, setScenarioId] = useState(pathScenarioId || null);
  const [consent, setConsent] = useState(() => !!localStorage.getItem(CONSENT_KEY));
  const [session, setSession] = useState(null); // {session_id, persona_name, opening_message, ...}
  const [messages, setMessages] = useState([]); // [{role:'them'|'me'|'system', text, flag?}]
  const [userTurns, setUserTurns] = useState([]); // for finish scoring
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finishResult, setFinishResult] = useState(null);
  const [remaining, setRemaining] = useState({ today: null, month: null });
  const [isPaid, setIsPaid] = useState(false);
  const [quotaError, setQuotaError] = useState(null);
  const threadRef = useRef(null);

  // Load config
  useEffect(() => {
    axios.get(`${API}/scamsmart/practice/config`).then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, finishResult]);

  // Auto-start when scenario chosen and consent given
  useEffect(() => {
    if (consent && scenarioId && !session && !starting) startSession(scenarioId);
  }, [consent, scenarioId]); // eslint-disable-line

  const acceptConsent = () => {
    localStorage.setItem(CONSENT_KEY, new Date().toISOString());
    setConsent(true);
  };

  const startSession = async (sid) => {
    setStarting(true);
    setQuotaError(null);
    setFinishResult(null);
    setMessages([]);
    setUserTurns([]);
    try {
      const { data } = await axios.post(`${API}/scamsmart/practice/start`, {
        scenario_id: sid,
        email: ssEmail || undefined,
      });
      setSession(data);
      setRemaining({ today: data.remaining_today, month: data.remaining_month });
      setIsPaid(data.is_paid);
      setMessages([{ role: 'them', text: data.opening_message }]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429) setQuotaError(detail);
      else if (e.response?.status === 503) setQuotaError('AI Practice Mode is temporarily unavailable.');
      else if (e.response?.status === 404) setQuotaError('This scenario doesn\'t have a live practice persona yet.');
      else setQuotaError('Could not start practice — please try again.');
    } finally {
      setStarting(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    // Read input directly from state OR fall back to the live DOM (some keyboards/automation
    // populate value without firing React's onChange — be defensive).
    const text = (input || '').trim();
    if (!text || sending || !session) return;
    setInput('');
    setMessages((m) => [...m, { role: 'me', text }]);
    setUserTurns((u) => [...u, text]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/scamsmart/practice/reply`, {
        session_id: session.session_id,
        scenario_id: scenarioId,
        user_message: text,
        email: ssEmail || undefined,
      });
      setMessages((m) => [...m, { role: 'them', text: data.reply, flag: data.safety_flag }]);
      setRemaining({ today: data.remaining_today, month: data.remaining_month });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setQuotaError(detail);
        setMessages((m) => [...m, { role: 'system', text: humanQuotaMsg(detail, isPaid) }]);
      } else {
        setMessages((m) => [...m, { role: 'system', text: 'The scammer simulation hit a snag — please try again.' }]);
      }
    } finally {
      setSending(false);
    }
  };

  const finish = async () => {
    if (!session || !userTurns.length) return;
    setFinishing(true);
    try {
      const { data } = await axios.post(`${API}/scamsmart/practice/finish`, {
        session_id: session.session_id,
        scenario_id: scenarioId,
        user_turns: userTurns,
        email: ssEmail || undefined,
      });
      setFinishResult(data);
    } catch (e) {
      setFinishResult({
        verdict: 'partial', rating: 60,
        summary: 'You held your own — keep practising the verify-first habit.',
        rule: 'When in doubt, verify through a trusted channel.',
        strongest_move: null, weakest_move: null,
      });
    } finally {
      setFinishing(false);
    }
  };

  const reset = () => {
    setSession(null);
    setMessages([]);
    setUserTurns([]);
    setFinishResult(null);
    setQuotaError(null);
  };

  const allPersonas = [
    { id: 'family-hi-mum-2026', name: "The 'Hi Mum' impostor", desc: "Scammer pretending to be your adult child from a new number — urgency + secrecy + cash transfer ask." },
    { id: 'bank-fraud-callback-2026', name: 'Fake bank fraud officer', desc: 'Calm scammer impersonating your bank\'s fraud team — wants you to read back a verification code.' },
    { id: 'delivery-auspost-fee-2026', name: 'Fake AusPost delivery', desc: '"$2.99 redelivery fee" SMS with a lookalike domain — pivots to a card-entry form.' },
    { id: 'marketplace-payid-overpay-2026', name: 'Marketplace PayID overpay', desc: 'Buyer claims they\'ll pay extra to cover a fake "business upgrade" fee — you\'re asked to pay first.' },
    { id: 'account-security-login-code-2026', name: 'Login-code thief', desc: '"Instagram Security" rings claiming a foreign login — wants the OTP you\'re about to receive.' },
    { id: 'marketplace-meet-and-pay-2026', name: 'Meet-and-disappear buyer', desc: 'Marketplace pickup who flashes a fake PayID transfer screenshot and asks to grab the item.' },
  ];

  if (!consent) {
    return (
      <ConsentScreen onAccept={acceptConsent} onBack={() => navigate(-1)} />
    );
  }

  if (!scenarioId) {
    return (
      <PersonaChooser
        personas={allPersonas}
        config={config}
        onPick={(id) => { setScenarioId(id); navigate(`/scamsmart/practice/${id}`); }}
        onBack={() => navigate('/scamsmart/dashboard')}
      />
    );
  }

  return (
    <div className="ss-page ssp-page">
      <header className="ss-header ssp-header">
        <div className="ss-header-inner">
          <Link to="/scamsmart/dashboard" className="ss-brand">
            <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">AI Practice Mode</div>
            </div>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ScamIQBadge />
          </div>
        </div>
      </header>

      <div className="ssp-shell">
        {/* Sticky disclaimer banner */}
        <div className="ssp-banner" data-testid="ssp-banner">
          <ShieldAlert size={14} />
          <span>
            <strong>Training simulation</strong> — the AI is role-playing a scammer for educational practice.
            Never share real account numbers, codes, or card details with anyone, including this tool.
          </span>
        </div>

        <div className="ssp-stage">
          <div className="ssp-stage-head">
            <button className="ssp-back" onClick={() => { reset(); navigate('/scamsmart/practice'); setScenarioId(null); }} data-testid="ssp-back">
              <ArrowLeft size={14} /> Switch scammer
            </button>
            <div className="ssp-stage-title">
              {session?.persona_name && <>You're facing: <strong>{session.persona_name}</strong></>}
            </div>
            <div className="ssp-quota">
              {remaining.today !== null && <>Today: {remaining.today} {isPaid ? '/ 30' : `/ ${config?.free_preview_msgs || 3} free`}</>}
            </div>
          </div>

          {starting && (
            <div className="ssp-loading" data-testid="ssp-loading">
              <Loader2 size={28} className="ssp-spin" />
              <p>Setting up the simulation…</p>
            </div>
          )}

          {quotaError && !session && (
            <div className="ssp-quota-block" data-testid="ssp-quota-block">
              <AlertTriangle size={20} />
              <h3>{humanQuotaMsg(quotaError, isPaid)}</h3>
              {!isPaid && (
                <Link to="/scamsmart" className="ssp-btn-primary">Unlock full access — $14.99</Link>
              )}
            </div>
          )}

          {session && !finishResult && (
            <div className="ssp-thread-wrap">
              <div className="ssp-thread" ref={threadRef} data-testid="ssp-thread">
                {messages.map((m, i) => (
                  <Bubble key={i} message={m} personaName={session.persona_name} />
                ))}
                {sending && (
                  <div className="ssp-bubble-row ssp-bubble-row-them">
                    <div className="ssp-bubble ssp-bubble-them ssp-bubble-typing" data-testid="ssp-typing">
                      <span /><span /><span />
                    </div>
                  </div>
                )}
              </div>

              <form className="ssp-composer" onSubmit={send} data-testid="ssp-composer">
                <input
                  type="text"
                  placeholder="Type your reply…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={sending || quotaError}
                  maxLength={600}
                  data-testid="ssp-input"
                />
                <button type="submit" disabled={!input.trim() || sending || quotaError} data-testid="ssp-send">
                  <Send size={16} />
                </button>
              </form>
              <div className="ssp-actions">
                <button className="ssp-btn-ghost" onClick={() => { if (window.confirm('Restart this scenario from the beginning?')) startSession(scenarioId); }} data-testid="ssp-restart">
                  <RotateCcw size={13} /> Restart
                </button>
                <button
                  className="ssp-btn-primary"
                  onClick={finish}
                  disabled={!userTurns.length || finishing}
                  data-testid="ssp-finish"
                >
                  {finishing ? <><Loader2 size={14} className="ssp-spin" /> Scoring…</> : <><Trophy size={14} /> Finish & get my score</>}
                </button>
              </div>
              <p className="ssp-fineprint">
                ScamSmart provides general scam-awareness training, not legal, financial, medical, or professional cybersecurity advice.
                For real scam recovery, contact <a href="tel:1800595160">IDCARE 1800 595 160</a> or your bank's 24h fraud line.
              </p>
            </div>
          )}

          {finishResult && (
            <ResultPanel
              result={finishResult}
              personaName={session?.persona_name}
              onAgain={() => startSession(scenarioId)}
              onSwitch={() => { reset(); navigate('/scamsmart/practice'); setScenarioId(null); }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
const Bubble = ({ message, personaName }) => {
  if (message.role === 'system') {
    return (
      <div className="ssp-system-row" data-testid="ssp-system">
        <AlertTriangle size={13} />
        <span>{message.text}</span>
      </div>
    );
  }
  if (message.role === 'me') {
    return (
      <div className="ssp-bubble-row ssp-bubble-row-me">
        <div className="ssp-bubble ssp-bubble-me">{message.text}</div>
      </div>
    );
  }
  const isFlagged = message.flag === 'distress' || message.flag === 'sensitive_data';
  return (
    <div className={`ssp-bubble-row ssp-bubble-row-them ${isFlagged ? 'ssp-bubble-row-flagged' : ''}`}>
      <div className="ssp-bubble-meta">{isFlagged ? 'ScamSmart safety' : personaName}</div>
      <div className={`ssp-bubble ssp-bubble-them ${isFlagged ? 'ssp-bubble-flagged' : ''}`}>{message.text}</div>
    </div>
  );
};

const humanQuotaMsg = (code, isPaid) => {
  switch (code) {
    case 'free_preview_used':
      return "You've used your free Practice Mode preview. Unlock full ScamSmart to keep training.";
    case 'user_daily_cap':
      return "You've hit today's Practice Mode quota. Your messages reset at midnight Sydney time.";
    case 'user_monthly_cap':
      return "You've hit this month's Practice Mode quota. It resets on the 1st.";
    case 'global_daily_cap':
    case 'global_monthly_usd_cap':
      return 'AI Practice Mode is at capacity today — please try again tomorrow.';
    default:
      return isPaid ? 'Practice Mode is briefly unavailable — try again in a minute.' : 'Practice Mode is currently unavailable.';
  }
};

// ============================================================
const ConsentScreen = ({ onAccept, onBack }) => (
  <div className="ss-page ssp-page">
    <header className="ss-header ssp-header">
      <div className="ss-header-inner">
        <Link to="/scamsmart/dashboard" className="ss-brand">
          <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
          <div><div className="ss-brand-text">All Secured</div><div className="ss-brand-sub">AI Practice Mode</div></div>
        </Link>
      </div>
    </header>
    <div className="ssp-consent-wrap" data-testid="ssp-consent">
      <div className="ssp-consent-card">
        <span className="ssp-consent-tag"><Sparkles size={12} /> One-time consent</span>
        <h1>Before you start practising</h1>
        <p>
          AI Practice Mode lets you have a back-and-forth conversation with an AI that's role-playing a scammer.
          It's a safe training simulator — used to help you and your family recognise scams before they happen.
        </p>
        <ul className="ssp-consent-list">
          <li><strong>This is educational training only.</strong> Nothing said in Practice Mode is real legal, financial, medical, or professional cybersecurity advice.</li>
          <li><strong>Don't share real account numbers, OTP codes, or card details</strong> with the AI — even if it asks. Use fake details like "BSB 000-000, Acc 12345678".</li>
          <li><strong>If something is actually happening to you right now</strong>, type something like "I've actually been scammed" and ScamSmart will break character and direct you to IDCARE, your bank, and Scamwatch.</li>
          <li><strong>Your replies are sent to a Claude AI</strong> (via Emergent's Universal Key) to generate the scammer's response. We don't sell your data.</li>
        </ul>
        <div className="ssp-consent-actions">
          <button className="ssp-btn-ghost" onClick={onBack} data-testid="ssp-consent-decline">Not now</button>
          <button className="ssp-btn-primary" onClick={onAccept} data-testid="ssp-consent-accept">
            I understand — start practising
          </button>
        </div>
        <p className="ssp-fineprint" style={{ marginTop: 18 }}>
          You only need to accept this once per device. You can revoke by clearing your browser storage.
        </p>
      </div>
    </div>
  </div>
);

// ============================================================
const PersonaChooser = ({ personas, config, onPick, onBack }) => (
  <div className="ss-page ssp-page">
    <header className="ss-header ssp-header">
      <div className="ss-header-inner">
        <Link to="/scamsmart/dashboard" className="ss-brand">
          <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
          <div><div className="ss-brand-text">All Secured</div><div className="ss-brand-sub">AI Practice Mode</div></div>
        </Link>
        <ScamIQBadge />
      </div>
    </header>
    <div className="ssp-shell">
      <button className="ssp-back" onClick={onBack} style={{ alignSelf: 'flex-start', marginBottom: 14 }}>
        <ArrowLeft size={14} /> Dashboard
      </button>
      <div className="ssp-chooser-intro">
        <span className="ssp-consent-tag"><Sparkles size={12} /> New · AI Practice Mode</span>
        <h1>Pick a scammer to practise against.</h1>
        <p>
          Each persona is a real, common Australian scam pattern. The AI will push, pivot, and apply pressure
          the way a real scammer would — but in a safe environment. You decide how to respond.
        </p>
      </div>
      <div className="ssp-chooser-grid" data-testid="ssp-chooser-grid">
        {personas.map((p) => {
          const available = config?.available_scenarios?.includes(p.id);
          return (
            <button
              key={p.id}
              className={`ssp-chooser-card ${available ? '' : 'ssp-chooser-card-disabled'}`}
              onClick={() => available && onPick(p.id)}
              disabled={!available}
              data-testid={`ssp-pick-${p.id}`}
            >
              <h3>{p.name}</h3>
              <p>{p.desc}</p>
              <span className="ssp-chooser-cta">{available ? <>Practise live <Send size={12} /></> : 'Coming soon'}</span>
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

// ============================================================
const ResultPanel = ({ result, personaName, onAgain, onSwitch }) => {
  const tone = result.verdict === 'defended' ? 'good' : result.verdict === 'partial' ? 'mid' : 'bad';
  return (
    <div className={`ssp-result ssp-result-${tone}`} data-testid="ssp-result">
      <div className="ssp-result-verdict">{VERDICT_LABEL[result.verdict] || 'Practice complete'}</div>
      <div className="ssp-result-rating">
        <span className="ssp-result-rating-num">{result.rating}</span>
        <span className="ssp-result-rating-suffix">/ 100</span>
      </div>
      <p className="ssp-result-summary">{result.summary}</p>
      <div className="ssp-result-rule">
        <span className="ssp-result-rule-tag"><Sparkles size={11} /> Rule to remember</span>
        <p>{result.rule}</p>
      </div>
      {(result.strongest_move || result.weakest_move) && (
        <div className="ssp-result-moves">
          {result.strongest_move && <div><span className="label">Strongest move</span><p>{result.strongest_move}</p></div>}
          {result.weakest_move && <div><span className="label">Weakest move</span><p>{result.weakest_move}</p></div>}
        </div>
      )}
      <div className="ssp-result-actions">
        <button className="ssp-btn-primary" onClick={onAgain} data-testid="ssp-result-again">
          <RotateCcw size={14} /> Try again
        </button>
        <button className="ssp-btn-ghost" onClick={onSwitch}>Switch scammer</button>
      </div>
    </div>
  );
};

export default ScamSmartPractice;
